import { Component, computed, inject, input, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import {
    PersonRestrictionDialogData,
    PersonRestrictionsDialogComponent,
} from '../person-restrictions-dialog/person-restrictions-dialog.component';
import { PersonRestrictionsStore } from './person-restrictions.store';
import { FilterInput } from '@it2go/data-grid';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { PersonContactTypeEnum } from '../../../../../../../../types/src/services/enum/person-contact-type.enum';
import { personContactFrag } from './person-restrictions.query';
import { userClientChipColumn } from '../../../../shared/components/kit/table/table.fields';
import { PersonContactGridItemObject } from '@it2go/types';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { CardTableComponent } from '../../../../shared/components/kit/table/card-table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-person-restrictions',
    templateUrl: './person-restrictions.component.html',
    styleUrls: ['./person-restrictions.component.scss'],
    providers: [PersonRestrictionsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardTableComponent,
        PrimeTemplate,
        TranslatePipe,
    ],
})
export class PersonRestrictionsComponent {

    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);
    public readonly store = inject(PersonRestrictionsStore);

    public personId = input<number | null>(null);
    public canEdit = input(true);

    public created = output<object>();

    protected personRestrictionTable = viewChild<TableComponent>('personRestrictionTable');

    protected items: any[] = [];

    protected additionalFilters = computed<FilterInput[][]>(() => [
        [{
            column: 'type',
            operator: 'EQ',
            value: [PersonContactTypeEnum.RESTRICTION],
        }],
        [{
            column: 'personId',
            operator: 'eq',
            value: [String(this.personId() || 0)],
        }],
    ]);

    protected columns: TableColumn[] = [
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
        },
        {
            ...userClientChipColumn,
            name: 'representative',
            dataKey: 'user',
        },
        {
            name: 'restrictionType',
        },

    ];

    constructor() {
        this.store.onSuccess(
            () => this.personRestrictionTable()?.reload(),
            'createPersonContact',
            'updatePersonContact',
            'deletePersonContact',
        );
    }

    protected async personRestrictionsDetail(item?: PersonContactGridItemObject): Promise<void> {
        const data: PersonRestrictionDialogData = item ? {
            restriction: item,
            personId: this.personId(),
        } : { personId: this.personId() };

        this.dialogService.open(PersonRestrictionsDialogComponent, {
            maximizable: true,
            data: data,
            baseZIndex: 2000,
            focusOnShow: false,
        }).onClose.subscribe((restrictions) => {
            if (!restrictions) return;

            this.created.emit(restrictions);

            this.items = !this.personId() ? [
                ...this.items,
                restrictions,
            ] : [];
        });
    }

    protected deletePersonRestriction(item: any): void {
        this.confirm.confirmDelete(
            () => {
                if (this.personId()) {
                    this.store.api.deletePersonContact({ id: item.item.id });
                } else {
                    this.items = [...this.items.filter((_, index) => index !== item.rowIndex)];
                }
            },
            item,
        );
    }

    protected readonly personContactFrag = personContactFrag;
}
